import { ReactComponent as SvgDropdownNav } from "assets/svg/dropdown-nav.svg";
import {
  QuestionCardList,
  QuestionModalProps,
  QuestionTypeMeta,
} from "./BaseQuestionModal";
import styled, { css } from "styled-components";
import { FlexColumn } from "Components/Flex";
import { useMemo, useState, useRef, useEffect } from "react";
import { SvgHelp } from "melodies-source/Svgs/Help";
import { Body2, Error, Label, LinkText } from "melodies-source/Text";
import { TextInput } from "Components";
import { AddQuestionFooter } from "./Footer";
import { ReactComponent as SvgSeries } from "assets/svg/series.svg";
import { Button } from "melodies-source/Button";
import { SvgAddAlt } from "melodies-source/Svgs/AddAlt";
import { CenterFlexRow } from "Routes/styled";
import { Selectable } from "melodies-source/Selectable";
import {
  DropdownModule,
  MultiSelectQuestionModule,
  SingleSelectQuestionModule,
} from "@max/common/dist/setfan";
import { SvgCloseLarge } from "melodies-source/Svgs/CloseLarge";
import { Tooltip } from "melodies-source/Tooltip";
import { QuestionCard } from "../../QuestionCard";
import { useBuilderContext } from "../../BuilderContext";
import { useConfirm } from "../../ConfirmContext";
import { confirmDeleteQuestionOption } from "Routes/SetFan/constants/confirmationContent";
import { SvgShow } from "melodies-source/Svgs/Show";
import { SvgHide } from "melodies-source/Svgs/Hide";
import { Label as InputLabel } from "Components";
import { DndContainer } from "Components/Sortable";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent, DragOverlay } from "@dnd-kit/core";
import { SortableRow } from "../../SortableRow";

const MIN_VISIBLE_OPTIONS = 2;

export const MultipleChoiceModal = ({
  existing,
  ...props
}: Omit<QuestionModalProps<MultiSelectQuestionModule>, "state">) => {
  const [questionText, setQuestionText] = useState(existing?.label.en ?? "");
  const [options, setOptions] = useState<Option[]>(
    existing?.options
      ?.filter((opt) => !opt.userDefined)
      .map((opt) => ({
        id: opt.id || window.crypto.randomUUID(),
        value: opt.label.en,
        other: opt.userDefined,
        hidden: !!opt.hidden,
      })) ?? [
      { id: window.crypto.randomUUID(), value: "" },
      { id: window.crypto.randomUUID(), value: "" },
    ],
  );
  const [selectOnlyOneOption, setSelectOnlyOneOption] = useState(
    existing?.type !== "MultipleSelectQuestion",
  );
  const [addOtherOption, setAddOtherOption] = useState(
    existing?.options.some((opt) => opt.userDefined),
  );
  const [required, setRequired] = useState(!!existing?.required);
  const [errors, setErrors] = useState<ValidationErrors>({
    questionText: "",
    options: {},
  });
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { status } = useBuilderContext();
  const isLiveEdit = status === "live" && !!existing?.id;
  const confirmDelete = useConfirm();
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const useDropdownModule = selectOnlyOneOption && options.length > 7;

  const onAdd = (hide?: boolean) => {
    setValidationTriggered(true);

    // handle errors
    setErrors({
      options: options.reduce((acc, opt) => {
        if (!opt.value) {
          acc[
            opt.id
          ] = `Please enter an option (${MIN_VISIBLE_OPTIONS} minimum)`;
        }
        return acc;
      }, {}),
      questionText: !questionText
        ? "This is a required field and cannot be blank"
        : "",
    });

    if (
      errors.questionText ||
      Object.values(errors.options).reduce((acc, err) => (acc += err.length), 0)
    ) {
      return;
    }
    // end handle errors

    let otherOptions = [];
    // can't have an 'other' option with a dropdown
    if (addOtherOption && !useDropdownModule) {
      otherOptions.push({
        id:
          existing?.options.find((opt) => opt.userDefined)?.id ||
          window.crypto.randomUUID(),
        userDefined: true,
        label: { en: "Other" },
      });
    }

    // if the question type changes between a multi and single select, we need to assign
    // a new id (passing undefined) because the answer type changed (string -> string[]
    // or string[] -> string).
    const newModuleCompatible = (() => {
      if (
        selectOnlyOneOption &&
        (existing?.type === "Dropdown" ||
          existing?.type === "SingleSelectQuestion")
      ) {
        return true;
      }
      if (!selectOnlyOneOption && existing?.type === "MultipleSelectQuestion") {
        return true;
      }
      return false;
    })();

    const modulePayload = {
      id: newModuleCompatible ? existing.id : undefined,
      label: { en: questionText },
      options: [
        ...options.map((opt) => ({
          id: opt.id || window.crypto.randomUUID(),
          label: { en: opt.value },
          ...(opt.hidden && { hidden: opt.hidden }),
        })),
        ...otherOptions,
      ],
      // single select
      required: hide ? false : required,
      // multi select
      requiredMin: required ? 1 : undefined,
      hidden: hide ?? existing?.hidden ?? false,
    };

    const newModule = !selectOnlyOneOption
      ? new MultiSelectQuestionModule(modulePayload)
      : useDropdownModule
      ? new DropdownModule(modulePayload)
      : new SingleSelectQuestionModule(modulePayload);

    props.dispatch(
      existing
        ? {
            type: "updateModule",
            payload: { idToReplace: existing?.id, module: newModule },
          }
        : {
            type: "addModule",
            payload: { module: newModule },
          },
    );

    props.dispatch({ type: "closeModal" });
  };

  const handleToggleOptionVisibility = (idx: number) => {
    if (hiddenOptionsLengthLimitReached && !options[idx].hidden) {
      displayErrorMessage(
        `There must be at least ${MIN_VISIBLE_OPTIONS} visible options`,
      );
      return;
    }
    setOptions((currentOptions) =>
      currentOptions.map((option, index) => {
        if (index === idx) {
          return { ...option, hidden: !option.hidden };
        }
        return option;
      }),
    );
  };

  const handleRemove = (idx: number) => {
    if (hiddenOptionsLengthLimitReached && !options[idx].hidden) {
      displayErrorMessage(
        `There must be at least ${MIN_VISIBLE_OPTIONS} visible options`,
      );
      return;
    }
    if (isLiveEdit) {
      confirmDelete({
        title: "Delete Response Option?",
        message: confirmDeleteQuestionOption,
        options: { hide: true, delete: true },
        onDelete: () =>
          setOptions((opts) => opts.filter((o) => o.id !== options[idx].id)),
        onHide: () => handleToggleOptionVisibility(idx),
        isHidden: !!options[idx].hidden,
      });
      return;
    }
    setOptions((opts) => opts.filter((o) => o.id !== options[idx].id));
  };

  const labelChangeWarning = isLiveEdit && existing?.label.en !== questionText;

  const hiddenOptionsLengthLimitReached = useMemo(
    () =>
      options.filter((opt) => opt.hidden).length >=
      options.length - MIN_VISIBLE_OPTIONS,
    [options],
  );

  const displayErrorMessage = (message: string) => {
    setErrorMessage(message);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setErrorMessage("");
    }, 5000);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const from = options.findIndex(({ id }) => id === active.id);
      const to = options.findIndex(({ id }) => id === over.id);
      setOptions((options) => arrayMove(options, from, to));
    }
  };

  return (
    <>
      <QuestionCardList>
        <QuestionCard icon={<SvgHelp />} title="Question">
          <TextInput
            label="What is your question?"
            placeholder="Type your question..."
            value={questionText}
            onChange={(text) => {
              if (validationTriggered && !text) {
                setErrors((errors) => ({
                  ...errors,
                  questionText: "This is a required field and cannot be blank",
                }));
              }
              if (text) {
                setErrors((errors) => ({ ...errors, questionText: "" }));
              }
              setQuestionText(text);
            }}
            style={{ maxWidth: 550 }}
            hasError={!!errors.questionText}
            helperText={errors.questionText as string}
          />
          {labelChangeWarning && (
            <Body2 style={{ fontWeight: 500, marginTop: 4, maxWidth: 550 }}>
              NOTE: Editing this will update the question in your report. Both
              previous and future responses to this question will show under
              this updated text.
            </Body2>
          )}
        </QuestionCard>
        <QuestionCard icon={<SvgSeries />} title="Options">
          {!!errorMessage && (
            <Error
              style={{
                position: "relative",
                top: -10,
                maxWidth: 550,
              }}
            >
              There needs to be at least {MIN_VISIBLE_OPTIONS} visible options.
            </Error>
          )}
          <DndContainer
            items={options}
            onDragEnd={handleDragEnd}
            variant="column"
          >
            {({ activeId }) => {
              const optIndex = options.findIndex((o) => o.id === activeId);
              const opt = options[optIndex];
              return (
                <>
                  {options.map((opt, idx) => (
                    <SortableRow key={opt.id} id={opt.id}>
                      <OptionRow
                        {...{
                          opt,
                          idx,
                          options,
                          errors,
                          validationTriggered,
                          setErrors,
                          setOptions,
                          handleRemove,
                        }}
                        handleToggleOptionVisibility={
                          !isLiveEdit
                            ? undefined
                            : () => handleToggleOptionVisibility(idx)
                        }
                      />
                    </SortableRow>
                  ))}
                  <DragOverlay>
                    {activeId && (
                      <SortableRow key={`sort-${opt.id}`} id={opt.id}>
                        <OptionRow
                          {...{
                            opt,
                            idx: optIndex,
                            options,
                          }}
                        />
                      </SortableRow>
                    )}
                  </DragOverlay>
                </>
              );
            }}
          </DndContainer>
          <Button
            variant="tertiary"
            leftIcon={<SvgAddAlt style={{ color: "#1B0076" }} />}
            onClick={() =>
              setOptions((options) => [
                ...options,
                { id: window.crypto.randomUUID(), value: "" },
              ])
            }
            style={{ width: 117, marginBottom: 20 }}
          >
            <LinkText style={{ color: "#1B0076" }}>Add Option</LinkText>
          </Button>
          {useDropdownModule ? (
            <Tooltip
              parent={
                <Selectable
                  disabled={useDropdownModule}
                  variant="checkbox"
                  value={false}
                  onChange={setAddOtherOption}
                  label='Add "Other" Option. Allow your fans to enter their own response'
                />
              }
            >
              This is only available for fewer options (less than 8)
            </Tooltip>
          ) : (
            <Selectable
              disabled={useDropdownModule}
              variant="checkbox"
              value={addOtherOption}
              onChange={setAddOtherOption}
              label='Add "Other" Option. Allow your fans to enter their own response'
            />
          )}
        </QuestionCard>

        <FlexColumn style={{ marginBottom: 20 }}>
          <Label
            style={{ color: "var(--secondary-text-color)", marginBottom: 6 }}
          >
            How many options can your fans select?
          </Label>
          <RadioContainer>
            <Selectable
              variant="radio"
              value={selectOnlyOneOption}
              onChange={(selected) =>
                !!selected && setSelectOnlyOneOption(selected)
              }
              label="Select only one option"
              style={{ marginRight: 15 }}
            />
            <Selectable
              variant="radio"
              value={!selectOnlyOneOption}
              onChange={(selected) =>
                selected && setSelectOnlyOneOption(!selected)
              }
              label="Select more than one option"
            />
          </RadioContainer>
        </FlexColumn>
      </QuestionCardList>
      <AddQuestionFooter
        required={required}
        setRequired={setRequired}
        confirmLabel={existing ? "Update" : "Add"}
        confirmDisabled={
          !questionText || !!options.find((opt) => !opt.value?.length)
        }
        cancelLabel={existing ? "Cancel" : "Back"}
        onCancel={() =>
          props.dispatch({ type: existing ? "closeModal" : "openModal" })
        }
        onConfirm={onAdd}
        onHide={!isLiveEdit ? undefined : () => onAdd(!existing?.hidden)}
        hidden={existing?.hidden}
      />
    </>
  );
};

export const multipleChoice: QuestionTypeMeta = {
  icon: <SvgDropdownNav />,
  key: "multipleChoiceQuestion",
  header: "Multiple Choice",
  subHeader: "Give participants a selection of options to choose from",
  body: MultipleChoiceModal,
};

type ValidationErrors = {
  questionText: string;
  options: Record<string, string>;
};

type Option = {
  id: string;
  value: string;
  other?: boolean;
  hidden?: boolean;
};

const RadioContainer = styled(CenterFlexRow)`
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const RemoveContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-bottom: 12px;
  color: #666;
  svg {
    width: 24px;
    height: 24px;
    &:hover {
      cursor: pointer;
      color: #333;
    }
  }
`;

const InputRow = styled.div<{ disabled?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 24px 24px;
  margin-bottom: 16px;
  gap: 12px;
  align-items: flex-end;
  width: min(445px, 100%);

  ${(p) =>
    p.disabled &&
    css`
      ${InputLabel} {
        opacity: 0.4;
      }
    `};
`;

type OptionRowProps = {
  opt: Option;
  idx: number;
  options: Option[];
  errors?: ValidationErrors;
  validationTriggered?: boolean;
  setErrors?: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  setOptions?: React.Dispatch<React.SetStateAction<Option[]>>;
  handleToggleOptionVisibility?: (idx: number) => void;
  handleRemove?: (idx: number) => void;
};

// OptionRow component for rendering a single option row in the list
const OptionRow = ({
  opt,
  idx,
  options,
  errors,
  validationTriggered,
  setErrors,
  setOptions,
  handleToggleOptionVisibility,
  handleRemove,
}: OptionRowProps) => (
  <InputRow key={`${opt.id}-select-choice`} disabled={opt.hidden}>
    <TextInput
      disabled={opt.hidden}
      label={`Option ${idx + 1}`}
      value={opt.value}
      placeholder="Enter option"
      onChange={(text) => {
        if (validationTriggered && !text) {
          setErrors((errors) => ({
            ...errors,
            options: {
              ...errors.options,
              [opt.id]: `Please enter an option (${MIN_VISIBLE_OPTIONS} minimum)`,
            },
          }));
        }
        if (text) {
          setErrors((errors) => ({
            ...errors,
            options: {
              ...errors.options,
              [opt.id]: "",
            },
          }));
        }
        setOptions((options) => {
          const newOptions = [...options];
          newOptions[idx].value = text;
          return newOptions;
        });
      }}
      hasError={!!errors?.options[opt.id]}
      helperText={errors?.options[opt.id]}
    />
    {options.length > MIN_VISIBLE_OPTIONS && handleToggleOptionVisibility && (
      <Tooltip
        parent={
          <RemoveContainer onClick={() => handleToggleOptionVisibility(idx)}>
            {!opt.hidden ? <SvgShow /> : <SvgHide />}
          </RemoveContainer>
        }
      >
        {!opt.hidden ? "Hide Option" : "Unhide Option"}
      </Tooltip>
    )}
    {options.length > MIN_VISIBLE_OPTIONS && (
      <RemoveContainer onClick={() => handleRemove(idx)}>
        <SvgCloseLarge />
      </RemoveContainer>
    )}
  </InputRow>
);
